import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

/** Producer-editable text for the starting-soon (holding) card: headline, message, start-time line. */
export function HoldingTextEditor({ program, showTitle, scheduledFor, patchProgram, disabled }) {
  const saved = program?.holding || {};
  const countdownOn = saved.countdown !== false;
  const when = scheduledFor ? new Date(scheduledFor) : null;
  const whenLabel = when && !Number.isNaN(when.getTime()) ? when.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : null;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ headline: "", message: "", start_time: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft({ headline: saved.headline || "", message: saved.message || "", start_time: saved.start_time || "" });
  }, [saved.headline, saved.message, saved.start_time]);

  const dirty = ["headline", "message", "start_time"].some((k) => (draft[k] || "") !== (saved[k] || ""));
  const save = async () => {
    setSaving(true);
    try { await patchProgram({ holding: draft }); } finally { setSaving(false); }
  };
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const field = "w-full rounded-md border border-white/10 bg-black/30 px-2.5 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-[#c9772e] focus:outline-none disabled:opacity-50";

  return (
    <div className="mt-2" data-testid="holding-text-editor">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="holding-text-toggle"
        className="flex w-full items-center justify-between rounded-md px-1 py-1 text-xs text-zinc-400 transition hover:text-zinc-200">
        <span>Starting-soon text{(saved.headline || saved.message || saved.start_time) ? <span className="ml-1 text-[#e6a25f]">· customised</span> : null}</span>
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <div className="mt-1 space-y-2 rounded-lg border border-white/10 bg-black/20 p-3">
          <label className="block text-[11px] uppercase tracking-wide text-zinc-500">Headline
            <input value={draft.headline} onChange={set("headline")} maxLength={80} disabled={disabled} placeholder={showTitle || "Live Podcast"} className={`${field} mt-1`} data-testid="holding-headline-input" />
          </label>
          <label className="block text-[11px] uppercase tracking-wide text-zinc-500">Message
            <input value={draft.message} onChange={set("message")} maxLength={140} disabled={disabled} placeholder="We'll be right with you" className={`${field} mt-1`} data-testid="holding-message-input" />
          </label>
          <label className="block text-[11px] uppercase tracking-wide text-zinc-500">Start-time line
            <input value={draft.start_time} onChange={set("start_time")} maxLength={60} disabled={disabled} placeholder="Starting at 12:45 PM PT" className={`${field} mt-1`} data-testid="holding-start-time-input" />
          </label>
          <label className="flex items-start gap-2 pt-1 text-xs text-zinc-300">
            <input type="checkbox" checked={countdownOn} disabled={disabled} onChange={(e) => patchProgram({ holding: { countdown: e.target.checked } })} className="mt-0.5 accent-[#c9772e]" data-testid="holding-countdown-toggle" />
            <span>
              Live countdown to the scheduled start
              <span className="block text-[11px] text-zinc-500" data-testid="holding-countdown-hint">
                {whenLabel ? `Counts down to ${whenLabel} (change it via Edit show).` : "No show time set — use Edit show to add one and the countdown appears."}
              </span>
            </span>
          </label>
          <div className="flex items-center justify-between gap-2 pt-1">
            <button type="button" disabled={disabled || saving || !(saved.headline || saved.message || saved.start_time)} data-testid="holding-text-reset"
              onClick={() => patchProgram({ holding: { headline: "", message: "", start_time: "" } })}
              className="text-xs text-zinc-500 underline-offset-2 hover:text-zinc-300 hover:underline disabled:opacity-40">Reset to defaults</button>
            <button type="button" onClick={save} disabled={disabled || saving || !dirty} data-testid="holding-text-save"
              className="rounded-md bg-[#c9772e] px-3 py-1.5 text-xs font-semibold text-black transition hover:bg-[#e6a25f] disabled:opacity-40">{saving ? "Saving…" : "Save text"}</button>
          </div>
          <p className="text-[11px] text-zinc-600">Shows on the broadcast whenever no camera is on air. Leave a field empty to use the default.</p>
        </div>
      )}
    </div>
  );
}

export default HoldingTextEditor;
